import React, { forwardRef } from 'react';
import { FaExclamationTriangle, FaSyncAlt, FaCheck, FaCodeBranch, FaTimes } from 'react-icons/fa';

export const ConflictResolution = forwardRef(({
  conflictData,
  onAcceptCurrent, // Accetta la versione del server e annulla le modifiche locali
  onMerge, // Tenta di riapplicare le modifiche (logica backend/frontend TBD)
  onCancel, // Annulla l'operazione e chiude il messaggio di conflitto
  theme = {},
  columnProps = [], // Per mostrare i dettagli dei campi
  primaryKey
}, ref) => {
  if (!conflictData) return null;

  const themeColor = theme?.themeColor || 'primary';
  const { recordId, yourChanges, serverData, errorMessage } = conflictData;

  // Funzione helper per visualizzare le differenze (semplice)
  const renderDifferences = () => {
    if (!serverData || !yourChanges) return <p className="text-sm text-gray-600 dark:text-gray-400">Dati del server non disponibili per il confronto.</p>;

    const fields = Object.keys(yourChanges).filter(key => key !== primaryKey);
    const differences = fields.filter(field => String(yourChanges[field]) !== String(serverData[field]));

    if (differences.length === 0) {
      return <p className="text-sm text-green-600 dark:text-green-400">Nessuna differenza rilevabile nei campi modificati (potrebbe essere un conflitto di versione).</p>;
    }

    return (
      <ul className="list-disc list-inside space-y-1 text-sm max-h-40 overflow-y-auto border p-2 rounded bg-gray-50 dark:bg-gray-700/50">
        {differences.map(field => {
          const colLabel = columnProps.find(c => c.field === field)?.label || field;
          return (
            <li key={field} className="text-gray-700 dark:text-gray-300">
              <strong className="font-medium">{colLabel}:</strong>
              <span className="ml-1 text-red-600 dark:text-red-400 line-through">{String(serverData[field]) ?? 'N/D'}</span>
              <span className="mx-1">→</span>
              <span className="text-green-600 dark:text-green-400">{String(yourChanges[field]) ?? 'N/D'}</span>
            </li>
          );
        })}
      </ul>
    );
  };

  return (
    <div ref={ref} id={`conflict-${recordId}`} className={`mt-6 mb-8 p-4 md:p-6 bg-yellow-50 dark:bg-yellow-900/30 border-l-4 border-yellow-400 dark:border-yellow-600 shadow-md rounded-r-lg`}>
      <div className="flex items-start">
        <FaExclamationTriangle className={`h-6 w-6 text-yellow-500 dark:text-yellow-400 mr-3 flex-shrink-0`} />
        <div className="flex-grow">
          <h4 className="text-md font-semibold text-yellow-800 dark:text-yellow-200">Conflitto di Modifica Rilevato</h4>
          <p className="text-sm text-yellow-700 dark:text-yellow-300 mt-1">
            Il record (ID: {recordId}) è stato modificato da qualcun altro dopo che hai iniziato la modifica.
          </p>
          {errorMessage && <p className="text-xs text-red-600 dark:text-red-400 mt-1">Dettaglio: {errorMessage}</p>}

          <div className="mt-3">
            <h5 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Confronto Modifiche:</h5>
            {renderDifferences()}
          </div>

          <div className="mt-4 flex flex-col sm:flex-row sm:items-center sm:justify-end space-y-2 sm:space-y-0 sm:space-x-3">
            <button
              onClick={onCancel || onAcceptCurrent} // Se onCancel non c'è, usa onAcceptCurrent come fallback per chiudere
              className="px-3 py-1.5 text-xs border border-gray-400 dark:border-gray-600 rounded-md text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition flex items-center justify-center"
            >
              <FaTimes className="mr-1.5" />
              Annulla Modifiche
            </button>
            <button
              onClick={onAcceptCurrent}
              className="px-3 py-1.5 text-xs border border-blue-500 text-blue-600 dark:text-blue-400 dark:border-blue-600 rounded-md hover:bg-blue-50 dark:hover:bg-blue-900/30 transition flex items-center justify-center"
            >
              <FaCheck className="mr-1.5" />
              Accetta Versione Corrente (Perdi Modifiche)
            </button>
            {onMerge && ( // Mostra solo se la funzione onMerge è fornita
              <button
                onClick={onMerge}
                className={`px-3 py-1.5 text-xs bg-${themeColor}-600 text-white rounded-md hover:bg-${themeColor}-700 transition flex items-center justify-center`}
              >
                <FaCodeBranch className="mr-1.5" />
                Tenta Unione (Merge)
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
});